import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowUpDown,
  Check,
  ChevronDown,
  Clock,
  Copy,
  ExternalLink,
  Eye,
  Globe,
  GridIcon,
  Key,
  KeyRound,
  Link as LinkIcon,
  List,
  LockKeyhole,
  MoreHorizontal,
  Plus,
  Search,
  Share2 as ShareIcon,
  Shield,
  Star,
  Tag,
  TrashIcon,
  User,
  X as CloseIcon,
} from 'lucide-react';
import { VaultCrypto, PasswordAnalyzer } from '@password-manager/crypto';
import { MainLayout } from '../components/MainLayout';
import { Button } from '../components/Button';
import { SecureText } from '../components/SecureText';
import { ShareVaultItemModal } from '../components/ShareVaultItemModal';
import { VaultItemModal } from '../components/VaultItemModal';
import { useAuth } from '../contexts/AuthContext';
import { WebCryptoProvider } from '../crypto/WebCryptoProvider';
import api from '../api/client';
import { copyTextToClipboard } from '../utils/clipboard';
import { useLocation, useNavigate } from 'react-router-dom';

const SAFE_URL_PROTOCOLS = new Set(['http:', 'https:']);

const formatDate = (value) => {
  if (!value) { return '—'; }
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value));
};

const formatRelativeDate = (value) => {
  if (!value) { return '—'; }
  const difference = Date.now() - new Date(value).getTime();
  const minutes = Math.max(1, Math.floor(difference / 60000));
  if (minutes < 60) { return `${minutes} min ago`; }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) { return `${hours} hour${hours === 1 ? '' : 's'} ago`; }
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const maskUsername = (username) => {
  if (!username) { return '—'; }
  if (username.includes('@')) {
    const [local, domain] = username.split('@');
    return `${local.slice(0, 2)}***@${domain}`;
  }
  if (username.length <= 4) { return `${username.slice(0, 1)}***`; }
  return `${username.slice(0, 2)}***${username.slice(-1)}`;
};

const getSafeUrl = (value) => {
  if (!value) { return null; }
  try {
    const url = new URL(value);
    return SAFE_URL_PROTOCOLS.has(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
};

const ConfirmDialog = ({ item, busy, onCancel, onConfirm }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  return (
    <div className="modal-overlay confirm-modal-backdrop" role="presentation" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="modal-content confirm-dialog vault-delete-dialog" role="dialog" aria-modal="true" aria-labelledby="delete-item-title" onClick={(e) => e.stopPropagation()}>
        <div className="confirm-icon delete-icon">
          <TrashIcon size={24} />
        </div>
        <h2 id="delete-item-title">Delete {item?.plaintext?.title ? `"${item.plaintext.title}"` : 'this item'}?</h2>
        <p className="confirm-dialog-desc">
          This permanently removes the encrypted vault item. This action cannot be undone.
        </p>

        {item?.plaintext && (
          <div className="confirm-item-preview">
            <div className="confirm-item-icon">
              <LockKeyhole size={16} />
            </div>
            <div className="confirm-item-info">
              <span className="confirm-item-title">{item.plaintext.title || 'Untitled Item'}</span>
              {(item.plaintext.username || item.category_name || item.plaintext.category) && (
                <span className="confirm-item-subtitle">
                  {item.plaintext.username || item.category_name || item.plaintext.category}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="vault-modal-actions confirm-actions">
          <button type="button" className="btn-confirm-cancel" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn-confirm-delete" disabled={busy} onClick={onConfirm}>
            <TrashIcon size={15} />
            <span>{busy ? 'Deleting...' : 'Delete item'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const ReauthDialog = ({ action, busy, error, onClose, onPasskey, onPassword }) => {
  const [password, setPassword] = useState('');
  const label = action === 'delete' ? 'delete this password' : 'save these password changes';

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="modal-overlay confirm-modal-backdrop" role="presentation" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="modal-content confirm-dialog vault-reauth-dialog" onSubmit={(event) => { event.preventDefault(); onPassword(password); }} role="dialog" aria-modal="true" aria-labelledby="reauth-title" onClick={(e) => e.stopPropagation()}>
        <div className="confirm-icon reauth-icon"><Shield size={24} /></div>
        <h2 id="reauth-title">Verify your identity</h2>
        <p className="confirm-dialog-desc">Re-authenticate to {label}. Your master password is used locally to unlock key material.</p>
        <input className="input-field" type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Master password" autoComplete="current-password" autoFocus />
        {error && <div className="form-error" role="alert">{error}</div>}
        <div className="vault-modal-actions confirm-actions">
          <button type="button" className="btn-confirm-cancel" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn-confirm-passkey" onClick={onPasskey} disabled={busy}>Use passkey</button>
          <button type="submit" className="btn-confirm-primary" disabled={busy || !password}>
            <span>{busy ? 'Verifying...' : 'Verify'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

const EmptyState = ({ filtered, onAdd, onGenerate }) => (
  <div className="vault-empty-state glass-panel">
    <div className="vault-empty-icon"><LockKeyhole size={24} /></div>
    <h2>{filtered ? 'No matching passwords' : 'Your vault is empty'}</h2>
    <p>{filtered ? 'Try a different search or filter.' : 'Add your first password to get started.'}</p>
    {!filtered && <div className="button-row"><Button onClick={onAdd}><Plus size={16} /> Add password</Button><Button variant="ghost" onClick={onGenerate}><Key size={16} /> Generate password</Button></div>}
  </div>
);

const ItemHealth = ({ item }) => (
  <div className="vault-health-icons" aria-label="Password health">
    {item.health?.weak && <AlertTriangle size={15} color="var(--danger)" aria-label="Weak password" />}
    {item.health?.reused && <Key size={15} color="var(--warning)" aria-label="Reused password" />}
    {item.health?.old && <Clock size={15} color="var(--text-secondary)" aria-label="Old password" />}
  </div>
);

export default function Vault() {
  const { vaultKey, isSensitiveAuthFresh, reauthenticateWithMasterPassword, reauthenticateWithPasskey } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const vaultCrypto = useMemo(() => new VaultCrypto(WebCryptoProvider), []);
  const [vaultId, setVaultId] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedSecret, setSelectedSecret] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [itemFormOpen, setItemFormOpen] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);
  const [reauthAction, setReauthAction] = useState(null);
  const [reauthBusy, setReauthBusy] = useState(false);
  const [reauthError, setReauthError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [menuItemId, setMenuItemId] = useState(null);
  const [copyRequest, setCopyRequest] = useState(null);
  const [notice, setNotice] = useState('');
  const [searchQuery, setSearchQuery] = useState(() => new URLSearchParams(location.search).get('search') || '');
  const [sortBy, setSortBy] = useState('alpha_asc');
  const [filterBy, setFilterBy] = useState(location.pathname === '/vault/favorites' ? 'favorites' : 'all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [viewMode, setViewMode] = useState(() => window.localStorage.getItem('cipher-vault-view') || 'table');
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [showFabTooltip, setShowFabTooltip] = useState(true);

  // Dismiss open action menu on outside click or Escape key
  useEffect(() => {
    if (!menuItemId) return;
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.action-menu-wrap')) {
        setMenuItemId(null);
      }
    };
    const handleEscKey = (e) => {
      if (e.key === 'Escape') setMenuItemId(null);
    };
    window.addEventListener('click', handleOutsideClick);
    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('click', handleOutsideClick);
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [menuItemId]);

  const showNotice = useCallback((message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(''), 2600);
  }, []);

  const decryptItem = useCallback(async (item) => {
    const plaintext = await vaultCrypto.decryptItem(item.encrypted_data, vaultKey, item.id);
    return JSON.parse(plaintext);
  }, [vaultCrypto, vaultKey]);

  const loadVault = useCallback(async () => {
    if (!vaultKey) { return; }
    setLoading(true);
    setLoadError('');
    try {
      const [vaultsResponse, categoriesResponse] = await Promise.all([api.get('/vaults'), api.get('/categories')]);
      const vault = vaultsResponse.data?.[0];
      setCategories(categoriesResponse.data || []);
      if (!vault) {
        setVaultId(null);
        setItems([]);
        return;
      }
      setVaultId(vault.id);
      const { data: serverItems } = await api.get(`/vaults/${vault.id}/items`);
      const decryptedItems = await Promise.all(serverItems.map(async (item) => {
        try {
          const plaintext = await decryptItem(item);
          return { ...item, plaintext };
        } catch {
          return { ...item, plaintext: {}, error: true };
        }
      }));
      const analyzedItems = PasswordAnalyzer.analyzeVaultHealth(decryptedItems);
      const safeListItems = analyzedItems.map((item) => {
        const { password, ...metadata } = item.plaintext || {};
        void password;
        return { ...item, plaintext: metadata };
      });
      setItems(safeListItems);
      setSelectedItem((current) => current ? safeListItems.find((item) => item.id === current.id) || null : null);
    } catch (error) {
      setLoadError(error?.response?.data?.message || 'Unable to load your vault.');
    } finally {
      setLoading(false);
    }
  }, [decryptItem, vaultKey]);

  useEffect(() => { loadVault(); }, [loadVault]);

  useEffect(() => {
    const query = new URLSearchParams(location.search).get('search') || '';
    setSearchQuery(query);
    if (location.pathname === '/vault/favorites') { setFilterBy('favorites'); }
    if (location.pathname === '/vault' || location.pathname === '/vault/all') { setFilterBy('all'); }
  }, [location.pathname, location.search]);

  const processedItems = useMemo(() => {
    let result = [...items];
    const query = searchQuery.trim().toLowerCase();
    if (query) {
      result = result.filter((item) => [item.plaintext?.title, item.plaintext?.username, item.plaintext?.url].some((value) => (value || '').toLowerCase().includes(query)));
    }
    if (filterBy === 'favorites') { result = result.filter((item) => item.is_favorite); }
    if (filterBy === 'weak') { result = result.filter((item) => item.health?.weak); }
    if (filterBy === 'reused') { result = result.filter((item) => item.health?.reused); }
    if (filterBy === 'old') { result = result.filter((item) => item.health?.old); }
    if (categoryFilter !== 'all') { result = result.filter((item) => (item.category_id || item.plaintext?.category || '') === categoryFilter); }
    result.sort((left, right) => {
      if (sortBy === 'recent_updated') { return new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime(); }
      if (sortBy === 'recent_created') { return new Date(right.created_at).getTime() - new Date(left.created_at).getTime(); }
      const leftTitle = (left.plaintext?.title || '').toLowerCase();
      const rightTitle = (right.plaintext?.title || '').toLowerCase();
      return sortBy === 'alpha_desc' ? rightTitle.localeCompare(leftTitle) : leftTitle.localeCompare(rightTitle);
    });
    return result;
  }, [categoryFilter, filterBy, items, searchQuery, sortBy]);

  const getCategoryName = (item) => item.category_name || item.plaintext?.category || 'Uncategorized';

  const selectItem = async (item, requestedAction = null) => {
    setMenuItemId(null);
    setSelectedItem(item);
    setSelectedSecret(null);
    setSelectedLoading(true);
    setCopyRequest(null);
    try {
      const secret = await decryptItem(item);
      setSelectedSecret(secret);
      if (requestedAction === 'copy') { setCopyRequest(`${item.id}:copy`); }
    } catch {
      showNotice('This item could not be decrypted. Verify your vault is unlocked.');
    } finally {
      setSelectedLoading(false);
    }
  };

  const handleOpenEdit = async (item) => {
    setMenuItemId(null);
    setActionBusy(true);
    try {
      const plaintext = await decryptItem(item);
      setEditingItem({ ...item, plaintext });
      setItemFormOpen(true);
    } catch {
      showNotice('This item could not be opened for editing.');
    } finally {
      setActionBusy(false);
    }
  };

  const performSave = async (form) => {
    if (!vaultId || !vaultKey) { throw new Error('Vault is unavailable.'); }
    const id = editingItem?.id || window.crypto.randomUUID();
    const encryptedData = await vaultCrypto.encryptItem(JSON.stringify({
      title: form.title,
      url: form.url,
      username: form.username,
      password: form.password,
      category: form.category,
      notes: form.notes,
      totpSecret: form.totpSecret,
      customFields: form.customFields,
    }), vaultKey, id);
    if (editingItem) {
      await api.put(`/vaults/${vaultId}/items/${id}`, { encryptedData, itemType: editingItem.item_type, isFavorite: form.isFavorite });
      if (form.categoryId !== (editingItem.category_id || '')) {
        await api.put(`/vaults/${vaultId}/items/${id}/category`, { categoryId: form.categoryId || null });
      }
    } else {
      await api.post(`/vaults/${vaultId}/items`, { id, itemType: 'PASSWORD', encryptedData, categoryId: form.categoryId });
    }
    setEditingItem(null);
    setItemFormOpen(false);
    await loadVault();
    showNotice(editingItem ? 'Password updated securely.' : 'Password added securely.');
  };

  const handleSave = async (form) => {
    setSaveError('');
    if (editingItem?.id && !isSensitiveAuthFresh()) {
      setReauthAction({ type: 'save', payload: form });
      return;
    }
    setActionBusy(true);
    try {
      await performSave(form);
    } catch (error) {
      setSaveError(error?.response?.data?.message || error.message || 'Unable to save this password.');
    } finally {
      setActionBusy(false);
    }
  };

  const performDelete = async (itemToDelete = confirmItem) => {
    if (!vaultId || !itemToDelete) { return; }
    await api.delete(`/vaults/${vaultId}/items/${itemToDelete.id}`);
    setItems((current) => current.filter((item) => item.id !== itemToDelete.id));
    if (selectedItem?.id === itemToDelete.id) {
      setSelectedItem(null);
      setSelectedSecret(null);
    }
    setConfirmItem(null);
    showNotice('Encrypted item deleted.');
  };

  const requestDelete = (item) => {
    setMenuItemId(null);
    setConfirmItem(item);
  };

  const confirmDelete = async () => {
    if (!isSensitiveAuthFresh()) {
      setReauthAction({ type: 'delete', payload: confirmItem });
      setConfirmItem(null);
      return;
    }
    setActionBusy(true);
    try {
      await performDelete();
    } catch (error) {
      showNotice(error?.response?.data?.message || 'Unable to delete this password.');
    } finally {
      setActionBusy(false);
    }
  };

  const toggleFavorite = async (item) => {
    if (!vaultId) { return; }
    setMenuItemId(null);
    try {
      await api.put(`/vaults/${vaultId}/items/${item.id}`, { encryptedData: item.encrypted_data, itemType: item.item_type, isFavorite: !item.is_favorite });
      setItems((current) => current.map((entry) => entry.id === item.id ? { ...entry, is_favorite: !item.is_favorite } : entry));
      showNotice(item.is_favorite ? 'Removed from favorites.' : 'Added to favorites.');
    } catch (error) {
      showNotice(error?.response?.data?.message || 'Unable to update favorite.');
    }
  };

  const duplicateItem = async (item) => {
    setMenuItemId(null);
    setActionBusy(true);
    try {
      const plaintext = await decryptItem(item);
      await performSave({ ...plaintext, title: `${plaintext.title || 'Password'} copy`, categoryId: item.category_id, isFavorite: false });
      showNotice('Password duplicated securely.');
    } catch (error) {
      showNotice(error?.response?.data?.message || 'Unable to duplicate this password.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleReauth = async (method, password) => {
    setReauthBusy(true);
    setReauthError('');
    const pending = reauthAction;
    try {
      if (method === 'passkey') {
        const success = await reauthenticateWithPasskey();
        if (!success) { throw new Error('Passkey verification failed.'); }
      } else {
        await reauthenticateWithMasterPassword(password);
      }
      setReauthAction(null);
      if (pending?.type === 'save') {
        setActionBusy(true);
        try { await performSave(pending.payload); } finally { setActionBusy(false); }
      }
      if (pending?.type === 'delete') {
        setActionBusy(true);
        try { await performDelete(pending.payload); } finally { setActionBusy(false); }
      }
    } catch (error) {
      if (pending?.type === 'save') {
        setSaveError(error?.response?.data?.message || error?.message || 'Unable to save this password.');
        setReauthAction(null);
      } else {
        setReauthError(error?.message || 'Authentication failed.');
      }
    } finally {
      setReauthBusy(false);
    }
  };

  const copyUsername = async (item) => {
    setMenuItemId(null);
    await copyTextToClipboard(item.plaintext?.username || '');
    showNotice('Username copied.');
  };

  const openWebsite = (item) => {
    setMenuItemId(null);
    const url = getSafeUrl(item.plaintext?.url);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      showNotice('This item does not contain a safe website URL.');
    }
  };

  const openAdd = () => { setSaveError(''); setEditingItem(null); setItemFormOpen(true); };

  return (
    <MainLayout>
      <div className="page-header vault-page-header notes-hero-banner">
        <div className="notes-hero-content">
          <div>
            <h1 className="notes-hero-title">All Items</h1>
            <p className="notes-hero-subtitle">All items are end-to-end encrypted. Passwords never appear in this list.</p>
          </div>
          <div className="button-row">
            <Button variant="ghost" className="notes-hero-btn-secondary" onClick={() => navigate('/generator')}><Key size={16} /> Generate</Button>
            <Button className="notes-hero-btn-primary" onClick={openAdd}><Plus size={16} /> Add password</Button>
          </div>
        </div>
      </div>

      <div className="vault-toolbar vault-desktop-toolbar">
        <div className="vault-search-wrap">
          <Search size={17} />
          <input
            type="search"
            name="vault-content-filter-search"
            id="vault-content-filter-search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            data-form-type="other"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search vault..."
            aria-label="Search vault"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '2px', display: 'flex' }}
              title="Clear search"
            >
              <CloseIcon size={14} />
            </button>
          )}
        </div>
        <label className="vault-select-wrap"><span>Sort</span><select className="input-field" value={sortBy} onChange={(event) => setSortBy(event.target.value)}><option value="alpha_asc">A-Z</option><option value="alpha_desc">Z-A</option><option value="recent_updated">Recently updated</option><option value="recent_created">Recently created</option></select><ChevronDown size={15} /></label>
        <label className="vault-select-wrap"><span>Items</span><select className="input-field" value={filterBy} onChange={(event) => setFilterBy(event.target.value)}><option value="all">All items</option><option value="favorites">Favorites</option><option value="weak">Weak passwords</option><option value="reused">Reused passwords</option><option value="old">Old passwords</option></select><ChevronDown size={15} /></label>
        <label className="vault-select-wrap"><span>Category</span><select className="input-field" value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}><option value="all">All categories</option>{categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}{Array.from(new Set(items.map((item) => item.plaintext?.category).filter(Boolean))).filter((name) => !categories.some((category) => category.name === name)).map((name) => <option key={name} value={name}>{name}</option>)}</select><ChevronDown size={15} /></label>
        <div className="view-toggle" role="group" aria-label="Vault view"><button type="button" className={viewMode === 'table' ? 'active' : ''} onClick={() => { setViewMode('table'); window.localStorage.setItem('cipher-vault-view', 'table'); }} aria-label="Table view"><List size={17} /></button><button type="button" className={viewMode === 'grid' ? 'active' : ''} onClick={() => { setViewMode('grid'); window.localStorage.setItem('cipher-vault-view', 'grid'); }} aria-label="Grid view"><GridIcon size={17} /></button></div>
      </div>

      {/* Mobile Single-Row Filter Bar */}
      <div className="vault-mobile-filter-bar">
        <button
          type="button"
          className={`vault-mobile-icon-btn ${searchQuery || isMobileSearchOpen ? 'is-active' : ''}`}
          onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
          title="Search"
          aria-label="Search vault"
        >
          <Search size={17} />
          {searchQuery && <span className="vault-filter-dot" />}
        </button>
        <div
          className={`vault-mobile-icon-btn ${filterBy !== 'all' ? 'is-active' : ''}`}
          title="Filter items"
        >
          <Shield size={17} />
          {filterBy !== 'all' && <span className="vault-filter-dot" />}
          <select
            className="vault-filter-hidden-select"
            value={filterBy}
            onChange={(e) => setFilterBy(e.target.value)}
            aria-label="Filter items"
          >
            <option value="all">All items</option>
            <option value="favorites">Favorites</option>
            <option value="weak">Weak passwords</option>
            <option value="reused">Reused passwords</option>
            <option value="old">Old passwords</option>
          </select>
        </div>
        <div
          className={`vault-mobile-icon-btn ${categoryFilter !== 'all' ? 'is-active' : ''}`}
          title="Category"
        >
          <Tag size={17} />
          {categoryFilter !== 'all' && <span className="vault-filter-dot" />}
          <select
            className="vault-filter-hidden-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            aria-label="Filter by category"
          >
            <option value="all">All categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
            {Array.from(new Set(items.map((item) => item.plaintext?.category).filter(Boolean)))
              .filter((name) => !categories.some((category) => category.name === name))
              .map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
          </select>
        </div>
        <div
          className={`vault-mobile-icon-btn ${sortBy !== 'alpha_asc' ? 'is-active' : ''}`}
          title="Sort"
        >
          <ArrowUpDown size={17} />
          <select
            className="vault-filter-hidden-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="Sort items"
          >
            <option value="alpha_asc">A–Z</option>
            <option value="alpha_desc">Z–A</option>
            <option value="recent_updated">Recently updated</option>
            <option value="recent_created">Recently created</option>
          </select>
        </div>
        <div className="vault-mobile-view-toggle">
          <button
            type="button"
            className={viewMode === 'table' ? 'active' : ''}
            onClick={() => {
              setViewMode('table');
              window.localStorage.setItem('cipher-vault-view', 'table');
            }}
            aria-label="Table view"
          >
            <List size={16} />
          </button>
          <button
            type="button"
            className={viewMode === 'grid' ? 'active' : ''}
            onClick={() => {
              setViewMode('grid');
              window.localStorage.setItem('cipher-vault-view', 'grid');
            }}
            aria-label="Grid view"
          >
            <GridIcon size={16} />
          </button>
        </div>
        {(searchQuery || filterBy !== 'all' || categoryFilter !== 'all' || sortBy !== 'alpha_asc') && (
          <button
            type="button"
            className="vault-mobile-icon-btn vault-mobile-reset-btn"
            onClick={() => {
              setSearchQuery('');
              setFilterBy('all');
              setCategoryFilter('all');
              setSortBy('alpha_asc');
              setIsMobileSearchOpen(false);
            }}
            title="Clear filters"
            aria-label="Clear filters"
          >
            <CloseIcon size={15} />
          </button>
        )}
      </div>
      {isMobileSearchOpen && (
        <div className="vault-mobile-search-expand">
          <Search size={15} className="vault-mobile-search-inner-icon" />
          <input
            className="vault-mobile-search-input"
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search vault…"
            autoFocus
            autoComplete="off"
            aria-label="Search vault mobile"
          />
          <button
            type="button"
            className="vault-mobile-search-close"
            onClick={() => {
              setSearchQuery('');
              setIsMobileSearchOpen(false);
            }}
            aria-label="Close search"
          >
            <CloseIcon size={14} />
          </button>
        </div>
      )}

      {notice && <div className="vault-notice" role="status"><Check size={16} /> {notice}</div>}
      {loading ? <div className="vault-loading-state glass-panel"><div className="loading-spinner" /> Decrypting item metadata locally…</div> : loadError ? <div className="vault-error-state glass-panel"><AlertTriangle size={20} /><span>{loadError}</span><Button variant="ghost" onClick={loadVault}>Retry</Button></div> : processedItems.length === 0 ? <EmptyState filtered={items.length > 0} onAdd={openAdd} onGenerate={() => navigate('/generator')} /> : (
        <>
          {viewMode === 'table' ? (
            <div className="vault-table-panel glass-panel">
              <div className="vault-table-scroll">
                <table className="vault-table">
                  <thead>
                    <tr>
                      <th aria-label="Favorite" />
                      <th>Item</th>
                      <th>Username</th>
                      <th>Category</th>
                      <th>Updated</th>
                      <th>Sharing</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {processedItems.map((item) => (
                      <tr
                        key={item.id}
                        className={`${selectedItem?.id === item.id ? 'selected' : ''} ${menuItemId === item.id ? 'menu-active' : ''}`}
                        onClick={() => selectItem(item)}
                      >
                        <td>
                          <button
                            type="button"
                            className={`favorite-button ${item.is_favorite ? 'active' : ''}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleFavorite(item);
                            }}
                            aria-label={item.is_favorite ? 'Remove favorite' : 'Add favorite'}
                          >
                            {item.is_favorite ? <Star size={17} fill="currentColor" /> : <Star size={17} />}
                          </button>
                        </td>
                        <td>
                          <div className="vault-item-cell">
                            <div className="vault-item-icon">
                              <LockKeyhole size={16} />
                            </div>
                            <div>
                              <strong>{item.plaintext?.title || 'Untitled item'}</strong>
                              <span>{item.item_type || 'PASSWORD'} <ItemHealth item={item} /></span>
                            </div>
                          </div>
                        </td>
                        <td className="masked-value">{maskUsername(item.plaintext?.username)}</td>
                        <td><span className="category-chip">{getCategoryName(item)}</span></td>
                        <td title={formatDate(item.updated_at)}>{formatRelativeDate(item.updated_at)}</td>
                        <td><span className="sharing-chip"><ShareIcon size={13} /> Private</span></td>
                        <td>
                          <ActionMenu
                            item={item}
                            open={menuItemId === item.id}
                            onToggle={() => setMenuItemId(menuItemId === item.id ? null : item.id)}
                            onView={() => selectItem(item)}
                            onCopy={() => selectItem(item, 'copy')}
                            onCopyUsername={() => copyUsername(item)}
                            onOpen={() => openWebsite(item)}
                            onEdit={() => handleOpenEdit(item)}
                            onShare={() => {
                              setMenuItemId(null);
                              setShareTarget(item);
                            }}
                            onFavorite={() => toggleFavorite(item)}
                            onDuplicate={() => duplicateItem(item)}
                            onDelete={() => requestDelete(item)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="vault-grid">
              {processedItems.map((item) => (
                <article
                  key={item.id}
                  className={`vault-card glass-panel ${selectedItem?.id === item.id ? 'selected' : ''} ${menuItemId === item.id ? 'menu-active' : ''}`}
                  onClick={() => selectItem(item)}
                >
                  <div className="vault-card-top">
                    <div className="vault-item-icon">
                      <LockKeyhole size={18} />
                    </div>
                    <button
                      type="button"
                      className={`favorite-button ${item.is_favorite ? 'active' : ''}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleFavorite(item);
                      }}
                      aria-label={item.is_favorite ? 'Remove favorite' : 'Add favorite'}
                    >
                      {item.is_favorite ? <Star size={17} fill="currentColor" /> : <Star size={17} />}
                    </button>
                  </div>
                  <h3>{item.plaintext?.title || 'Untitled item'}</h3>
                  <p className="masked-value">{maskUsername(item.plaintext?.username)}</p>
                  <div className="vault-card-footer">
                    <span className="category-chip">{getCategoryName(item)}</span>
                    <span>{formatRelativeDate(item.updated_at)}</span>
                    <ActionMenu
                      item={item}
                      open={menuItemId === item.id}
                      onToggle={() => setMenuItemId(menuItemId === item.id ? null : item.id)}
                      onView={() => selectItem(item)}
                      onCopy={() => selectItem(item, 'copy')}
                      onCopyUsername={() => copyUsername(item)}
                      onOpen={() => openWebsite(item)}
                      onEdit={() => handleOpenEdit(item)}
                      onShare={() => {
                        setMenuItemId(null);
                        setShareTarget(item);
                      }}
                      onFavorite={() => toggleFavorite(item)}
                      onDuplicate={() => duplicateItem(item)}
                      onDelete={() => requestDelete(item)}
                    />
                  </div>
                </article>
              ))}
            </div>
          )}

        </>
      )}

      {/* Right-side Drawer for Item Details */}
      {selectedItem && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', top: 0, right: 0, width: '420px', maxWidth: '100vw', height: '100vh',
            background: 'var(--bg-primary, #ffffff)',
            boxShadow: '-6px 0 30px rgba(0,0,0,0.15)',
            zIndex: 100, display: 'flex', flexDirection: 'column',
            transform: 'translateX(0)', transition: 'transform 0.3s ease',
            borderLeft: '1px solid var(--border-color)',
          }}
        >
          {/* Drawer Header */}
          <div style={{ padding: '24px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--accent-primary)', marginBottom: '4px' }}>
                Secure Item
              </div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)' }}>
                {selectedItem.plaintext?.title || 'Untitled item'}
              </h2>
            </div>
            <button
              type="button"
              onClick={() => { setSelectedItem(null); setSelectedSecret(null); }}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px' }}
              aria-label="Close item details"
            >
              <CloseIcon size={20} />
            </button>
          </div>

          {/* Drawer Body */}
          <div style={{ padding: '24px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {selectedLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
                <div className="loading-spinner" style={{ marginRight: '10px' }} /> Opening encrypted item…
              </div>
            ) : selectedSecret ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={{ padding: '12px 14px', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Username</div>
                    <strong style={{ fontSize: '14px', wordBreak: 'break-all', color: 'var(--text-primary)' }}>{selectedSecret.username || '—'}</strong>
                  </div>
                  <div style={{ padding: '12px 14px', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Category</div>
                    <strong style={{ fontSize: '14px', wordBreak: 'break-all', color: 'var(--text-primary)' }}>{getCategoryName(selectedItem)}</strong>
                  </div>
                </div>

                {selectedSecret.url && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Website</div>
                    <a
                      href={getSafeUrl(selectedSecret.url) || '#'}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px',
                        borderRadius: '10px', border: '1px solid var(--border-color)',
                        background: 'var(--bg-secondary)', color: 'var(--accent-primary)',
                        textDecoration: 'none', fontSize: '13px', wordBreak: 'break-all',
                      }}
                    >
                      <Globe size={16} style={{ flexShrink: 0 }} />
                      <span style={{ flex: 1 }}>{selectedSecret.url}</span>
                      <ExternalLink size={14} style={{ opacity: 0.6 }} />
                    </a>
                  </div>
                )}

                {selectedSecret.password && (
                  <div>
                    <SecureText
                      key={`${selectedItem.id}-${copyRequest || 'view'}`}
                      label="Password"
                      value={selectedSecret.password}
                      autoAction={copyRequest ? 'copy' : null}
                      onCopied={() => showNotice('Password copied securely.')}
                    />
                  </div>
                )}

                {selectedSecret.notes && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Secure notes</div>
                    <div style={{
                      padding: '12px 14px', borderRadius: '10px', border: '1px solid var(--border-color)',
                      background: 'var(--bg-secondary)', whiteSpace: 'pre-wrap', fontSize: '13px', lineHeight: 1.5,
                      color: 'var(--text-primary)',
                    }}>
                      {selectedSecret.notes}
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 'auto', paddingTop: '16px', display: 'flex', gap: '10px' }}>
                  <Button type="button" variant="ghost" onClick={() => handleOpenEdit(selectedItem)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <Key size={15} /> Edit
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setShareTarget(selectedItem)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <ShareIcon size={15} /> Share
                  </Button>
                  <Button type="button" variant="danger" onClick={() => requestDelete(selectedItem)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 12px' }} title="Delete">
                    <TrashIcon size={16} />
                  </Button>
                </div>
              </>
            ) : (
              <div className="vault-error-state">Unable to decrypt this item.</div>
            )}
          </div>
        </div>
      )}

      {/* Drawer Backdrop */}
      {selectedItem && (
        <div
          onClick={() => { setSelectedItem(null); setSelectedSecret(null); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)',
            zIndex: 90, backdropFilter: 'blur(2px)',
          }}
        />
      )}

      <VaultItemModal open={itemFormOpen} item={editingItem?.id ? editingItem : null} categories={categories} onClose={() => { setItemFormOpen(false); setEditingItem(null); setSaveError(''); }} onSave={handleSave} loading={actionBusy} error={saveError} />
      <ShareVaultItemModal open={Boolean(shareTarget)} item={shareTarget} onClose={() => setShareTarget(null)} onCreated={() => { setShareTarget(null); showNotice('Secure share created.'); }} />
      {confirmItem && <ConfirmDialog item={confirmItem} busy={actionBusy} onCancel={() => setConfirmItem(null)} onConfirm={confirmDelete} />}
      {reauthAction && <ReauthDialog action={reauthAction.type} busy={reauthBusy} error={reauthError} onClose={() => { setReauthAction(null); setReauthError(''); }} onPasskey={() => handleReauth('passkey')} onPassword={(password) => handleReauth('password', password)} />}
      {/* ══════════════════════════════════════════════════════════════
          BOTTOM-RIGHT FLOATING ACTION BUTTON (iOS / GEMINI FAB)
          - Tap to add password to vault (same as Dashboard FAB)
          ══════════════════════════════════════════════════════════════ */}
      <div className="v-fab-container vault-fab-container" role="region" aria-label="Quick Actions">
        {showFabTooltip && (
          <div
            className="v-fab-tooltip animate-pop-in"
            onClick={openAdd}
            title="Tap to add password"
          >
            <span className="v-fab-tooltip-icon">🔑</span>
            <div className="v-fab-tooltip-text">
              <strong>Tap to add password</strong>
            </div>
            <button
              type="button"
              className="v-fab-tooltip-close"
              onClick={(e) => {
                e.stopPropagation();
                setShowFabTooltip(false);
              }}
              aria-label="Close hint"
            >
              <CloseIcon size={12} />
            </button>
          </div>
        )}

        <button
          type="button"
          className="v-fab-button v-fab-vault-btn"
          onClick={openAdd}
          title="Tap to Add Password"
          aria-label="Tap to add password to vault"
          style={{
            borderRadius: '50%',
            WebkitBorderRadius: '50%',
            width: '58px',
            height: '58px',
            minWidth: '58px',
            minHeight: '58px',
            maxWidth: '58px',
            maxHeight: '58px',
            aspectRatio: '1 / 1',
            overflow: 'hidden',
            padding: 0
          }}
        >
          <div className="v-fab-icon-wrapper">
            <div className="v-fab-icon-face v-fab-icon-front">
              <KeyRound size={24} strokeWidth={2.4} color="#ffffff" />
              <span className="v-fab-mini-badge" style={{ color: '#0284c7' }}>+</span>
            </div>
          </div>
        </button>
      </div>
    </MainLayout>
  );
}

const ActionMenu = ({ item, open, onToggle, onView, onCopy, onCopyUsername, onOpen, onEdit, onShare, onFavorite, onDuplicate, onDelete }) => {
  const [openUpward, setOpenUpward] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (open && wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      // If less than 320px below and enough space above, flip upward
      if (spaceBelow < 320 && rect.top > 240) {
        setOpenUpward(true);
      } else {
        setOpenUpward(false);
      }
    }
  }, [open]);

  return (
    <div
      ref={wrapRef}
      className={`action-menu-wrap ${open ? 'open' : ''}`}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="icon-button"
        onClick={onToggle}
        aria-label={`Actions for ${item.plaintext?.title || 'item'}`}
        aria-expanded={open}
      >
        <MoreHorizontal size={18} />
      </button>

      {open && (
        <>
          <div
            className="action-menu-backdrop"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          />
          <div
            className={`action-menu ${openUpward ? 'open-upward' : ''}`}
            role="menu"
          >
            <div className="action-menu-item-header">
              <div className="action-menu-item-title">{item.plaintext?.title || 'Item'}</div>
              {item.plaintext?.username && <div className="action-menu-item-sub">{item.plaintext.username}</div>}
            </div>
            <div className="action-menu-item-divider" />
            <button type="button" onClick={() => { onToggle(); onView(); }}><Eye size={15} /> View</button>
            <button type="button" onClick={() => { onToggle(); onCopy(); }}><Copy size={15} /> Copy password</button>
            <button type="button" onClick={() => { onToggle(); onCopyUsername(); }}><User size={15} /> Copy username</button>
            <button type="button" onClick={() => { onToggle(); onOpen(); }}><ExternalLink size={15} /> Open website</button>
            <button type="button" onClick={() => { onToggle(); onEdit(); }}><Key size={15} /> Edit</button>
            <button type="button" onClick={() => { onToggle(); onShare(); }}><ShareIcon size={15} /> Share</button>
            <button type="button" onClick={() => { onToggle(); onFavorite(); }}><Star size={15} /> {item.is_favorite ? 'Remove favorite' : 'Add favorite'}</button>
            <button type="button" onClick={() => { onToggle(); onDuplicate(); }}><Copy size={15} /> Duplicate</button>
            <button type="button" className="danger-action" onClick={() => { onToggle(); onDelete(); }}><TrashIcon size={15} /> Delete</button>
            <div className="action-menu-cancel-row">
              <button type="button" className="action-menu-cancel-btn" onClick={onToggle}>Cancel</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
